import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

interface ThemeToggleProps {
  className?: string;
  showLabel?: boolean;
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({ 
  className = '', 
  showLabel = true 
}) => {
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {showLabel && (
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('settings.theme')}
        </span>
      )}
      
      <button
        onClick={toggleTheme}
        className={`
          relative inline-flex h-10 w-11 items-center rounded-lg border transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800
          ${theme === 'dark' 
            ? 'bg-blue-600 hover:bg-blue-700 border-blue-600' 
            : 'bg-gray-200 hover:bg-gray-300 border-gray-300 dark:border-gray-600'
          }
        `}
        role="switch"
        aria-checked={theme === 'dark'}
        aria-label={theme === 'dark' ? t('settings.lightMode') : t('settings.darkMode')}
      >
        <span
          className={`
            inline-block h-6 w-6 transform rounded-md bg-white transition-transform duration-200 ease-in-out shadow-lg
            ${theme === 'dark' ? 'translate-x-4' : 'translate-x-1'}
          `}
        />
        
        {/* Ícones dentro do toggle */}
        <span className="absolute inset-0 flex items-center justify-between px-2">
          {/* Ícone do sol (modo claro) */}
          <svg
            className={`h-4 w-4 transition-opacity duration-200 ${
              theme === 'light' ? 'opacity-100 text-yellow-500' : 'opacity-0'
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
              clipRule="evenodd"
            />
          </svg>
          
          {/* Ícone da lua (modo escuro) */}
          <svg
            className={`h-4 w-4 transition-opacity duration-200 ${
              theme === 'dark' ? 'opacity-100 text-blue-300' : 'opacity-0'
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
          </svg>
        </span>
      </button>
      
      {showLabel && (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {theme === 'dark' ? t('settings.darkMode') : t('settings.lightMode')}
        </span>
      )}
    </div>
  );
};